import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { CreateEstimationStepsEnum } from "./estimationTypes";
import { PlantHireCategory, ServicePriceType } from "../Services/serviceTypes";

// --- TYPES ---
export interface ServiceCartItem {
  _id: string;
  name: string;
  price_type: ServicePriceType;
  unit_price: number;
  image: string;
  is_hire_service: boolean;
  plant_hire_category?: PlantHireCategory | null;
  qty: number;
  days: number;
  total_price: number | null;
  slug?: string;
  installation_date?: string | null;
  is_edited?: boolean;
  is_date_required: boolean;
  date_label: string;
}

export interface SelectedStorageType {
  _id: string;
  category_id: string;
  name: string;
  image: string;
  slug: string;
  product_type_name?: string;
  summary?: any;
}

export interface EstimationDetails {
  _id: string;
  estimation_id?: string;
  display_estimation_id?: string;
  status: string;
  currency?: string;
  conversion_rate?: number;
  last_completed_step?: string;
  categories_snapshot?: any[];
  services?: any[];
  services_total?: number;

  customer_snapshot?: {
    company_name: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    vat_number: string;
    vat_type?: string;
    billing_address: any;
    delivery_address: any;
    preferences?: {
      color: string;
      design_preview: boolean;
    };
    show_retail_price?: boolean;
    [key: string]: any;
  };

  customer_id?: any;
}

// --- GLOBAL STATE STRUCTURE ---
interface EstimationState {
  estimationId: string | null;
  estimationDetails: EstimationDetails | null;
  selectedStorageTypes: SelectedStorageType[];
  servicesCart: ServiceCartItem[];
  loading: boolean;
}

const initialState: EstimationState = {
  estimationId: null,
  estimationDetails: null,
  selectedStorageTypes: [],
  servicesCart: [],
  loading: false,
};

const estimationSlice = createSlice({
  name: "estimation",
  initialState,
  reducers: {
    setEstimationId: (state, action: PayloadAction<string | null>) => {
      state.estimationId = action.payload;
    },

    setSelectedStorageTypes: (
      state,
      action: PayloadAction<SelectedStorageType[]>,
    ) => {
      state.selectedStorageTypes = action.payload;
    },

    setEstimationDetails: (
      state,
      action: PayloadAction<EstimationDetails>
    ) => {
      state.estimationDetails = action.payload;
      if (action.payload) {
        state.estimationId =
          action.payload.estimation_id || action.payload._id;
        state.selectedStorageTypes = (
          action.payload.categories_snapshot ?? []
        ).map((item) => ({
          ...item,
          _id: item._id,
          category_id: item.category_id,
          name: item.name,
          image: item.image,
          slug: item.slug,
          product_type_name: item.product_type_name,
          summary: item.summary,
        }));
      }
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    // --- SERVICE CART ACTIONS ---
    setServicesCart: (state, action: PayloadAction<ServiceCartItem[]>) => {
      state.servicesCart = action.payload;
    },

    addServiceItem: (state, action: PayloadAction<ServiceCartItem>) => {
      const exists = state.servicesCart.find(
        (item) => item._id === action.payload._id,
      );
      if (!exists) {
        state.servicesCart.push(action.payload);
      }
    },

    updateServiceItem: (
      state,
      action: PayloadAction<{ id: string; changes: Partial<ServiceCartItem> }>,
    ) => {
      const { id, changes } = action.payload;
      const index = state.servicesCart.findIndex((item) => item._id === id);
      if (index !== -1) {
        const updatedItem = { ...state.servicesCart[index], ...changes };

        // Recalculate Price Logic based on price_type
        if (changes.qty !== undefined || changes.days !== undefined) {
          const { price_type, unit_price, qty, days } = updatedItem;
          if (price_type === ServicePriceType.DAY_PERSON) {
            updatedItem.total_price = unit_price * qty * days;
          } else if (price_type === ServicePriceType.DAY) {
            updatedItem.total_price = unit_price * days * qty;
          } else if (price_type === ServicePriceType.BRACING) {
            updatedItem.total_price = unit_price * qty;
          } else if (price_type === ServicePriceType.PER_QTY) {
            updatedItem.total_price = unit_price * qty;
          }
        }
        state.servicesCart[index] = updatedItem;
      }
    },

    removeServiceItem: (state, action: PayloadAction<string>) => {
      state.servicesCart = state.servicesCart.filter(
        (item) => item._id !== action.payload,
      );
    },

    resetServicesCart: (state) => {
      state.servicesCart = [];
    },

    resetEstimationState: () => initialState,
  },
});

export const {
  setEstimationId,
  setSelectedStorageTypes,
  setEstimationDetails,
  setLoading,
  setServicesCart,
  addServiceItem,
  updateServiceItem,
  removeServiceItem,
  resetServicesCart,
  resetEstimationState,
} = estimationSlice.actions;

// --- SELECTORS ---
export const selectEstimationId = (state: any) =>
  state.estimation.estimationId;
export const selectEstimationDetails = (state: any) =>
  state.estimation.estimationDetails;
export const selectSelectedStorageTypes = (state: any) =>
  state.estimation.selectedStorageTypes;
export const selectEstimationLoading = (state: any) =>
  state.estimation.loading;
export const selectEstimationLastCompletedStep = (state: any) =>
  state.estimation.estimationDetails?.last_completed_step ??
  CreateEstimationStepsEnum.CUSTOMER_DETAILS;

export const selectServicesCart = (state: any) => state.estimation.servicesCart;
export const selectServicesTotal = (state: any) =>
  state.estimation.servicesCart.reduce(
    (sum: number, item: ServiceCartItem) => sum + (item.total_price || 0),
    0,
  );

// --- STEP PLAYLIST ---
export const STATIC_ESTIMATION_GROUP_CHILDREN = [
  { key: "floor-details", label: "Floor Details" },
];

export const calculateEstimationStepPlaylist = (
  selectedStorageTypes: SelectedStorageType[],
  estimationDetails?: EstimationDetails | null,
): string[] => {
  const playlist: string[] = [
    CreateEstimationStepsEnum.CUSTOMER_DETAILS,
    CreateEstimationStepsEnum.STORAGE_TYPE,
  ];

  selectedStorageTypes.forEach((cat) => {
    STATIC_ESTIMATION_GROUP_CHILDREN.forEach((child) => {
      playlist.push(`${cat.category_id || cat._id}__${child.key}`);
    });
  });

  playlist.push(
    CreateEstimationStepsEnum.SERVICES,
    CreateEstimationStepsEnum.DELIVERY,
    CreateEstimationStepsEnum.NOTES,
    CreateEstimationStepsEnum.DESIGN,
  );

  const showSiteSurveyStatuses = [
    "approved",
    "site-survey-pending",
    "pending-manufacturing-quote",
    "design-approval-require",
    "ready-for-quote",
    "quote-created",
  ];

  if (estimationDetails?.status && showSiteSurveyStatuses.includes(estimationDetails.status)) {
    playlist.push(CreateEstimationStepsEnum.SITE_SURVEY);
  }

  playlist.push(CreateEstimationStepsEnum.REVIEW);

  return playlist;
};

export const selectEstimationStepPlaylist = createSelector(
  [selectSelectedStorageTypes, selectEstimationDetails],
  (selectedStorageTypes: SelectedStorageType[], estimationDetails: EstimationDetails | null) =>
    calculateEstimationStepPlaylist(selectedStorageTypes, estimationDetails),
);

export default estimationSlice.reducer;

